import { comparer } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { BaseSelect } from "./BaseSelect";
import { useFormContext } from "./Form";
import { StaticMultipleValues } from "./StaticInput";
import { DEFAULT_ID, withFormWrapper } from "./withFormWrapper";
// stable so it can appear in hook dependency arrays without defeating them
var EMPTY_MULTI_VALUE = [];
/**
 * The original piston form (MobX) select behavior: reads and writes the
 * form value via `useFormContext`, resolves the selected option
 * structurally, and renders `BaseSelect` for the live control.
 */
export var LegacySelect = observer(function (_a) {
    var options = _a.options, components = _a.components, name = _a.name, onChange = _a.onChange, _b = _a.multiple, multiple = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.autoFocus, autoFocus = _d === void 0 ? false : _d, _e = _a.loading, loading = _e === void 0 ? false : _e, formatter = _a.formatter, placeholder = _a.placeholder, _f = _a.uniqueId, uniqueId = _f === void 0 ? DEFAULT_ID : _f, label = _a.label, staticFormatter = _a.staticFormatter, _g = _a.isClearable, isClearable = _g === void 0 ? true : _g, dataId = _a.dataId, dataLabel = _a.dataLabel, dataTestId = _a.dataTestId, ariaLabelledby = _a.ariaLabelledby, _h = _a.creatable, creatable = _h === void 0 ? false : _h, portal = _a.portal;
    var _j = useFormContext(), values = _j.values, formDisabled = _j.formDisabled, isStatic = _j.isStatic;
    var emptyValue = multiple ? EMPTY_MULTI_VALUE : null;
    var selectionValue = values.get(name);
    var currentValue = React.useMemo(function () {
        if (multiple) {
            return selectionValue
                ? selectionValue.map(function (v) {
                    return options.find(function (o) { return comparer.structural(o.value, v); });
                })
                : emptyValue;
        }
        var value = options.find(function (o) {
            return comparer.structural(o.value, selectionValue);
        });
        return value !== undefined ? value : emptyValue;
    }, [multiple, selectionValue, options, emptyValue]);
    var getValueOfSelected = React.useCallback(function (selected) {
        if (selected === null || selected === undefined) {
            return emptyValue;
        }
        if (multiple) {
            return selected.map(function (o) { return o.value; });
        }
        return selected.value;
    }, [multiple, emptyValue]);
    var getLabelOfSelected = React.useCallback(function (selected) {
        if (selected === null || selected === undefined) {
            return emptyValue;
        }
        if (multiple) {
            return selected.map(function (o) { return o.label; });
        }
        return selected.label;
    }, [multiple, emptyValue]);
    var getLabelsFromValues = React.useCallback(function (value, options) {
        if (value === null || value === undefined) {
            return emptyValue;
        }
        if (multiple) {
            var selected_1 = options.filter(function (o) { return value.includes(o.value); });
            return selected_1.map(function (o) { return o.label; });
        }
        var selected = options.find(function (o) { return o.value === value; });
        return selected ? selected.label : emptyValue;
    }, [multiple, emptyValue]);
    var handleChange = React.useCallback(function (selected) {
        if (onChange) {
            var newValue = getValueOfSelected(selected);
            var oldValue = selectionValue;
            var newLabel = getLabelOfSelected(selected);
            var oldLabel = getLabelsFromValues(selectionValue, options);
            onChange(newValue, oldValue, newLabel, oldLabel);
        }
        values.set(name, getValueOfSelected(selected));
    }, [
        onChange,
        values,
        selectionValue,
        options,
        getValueOfSelected,
        getLabelOfSelected,
        getLabelsFromValues,
        name,
    ]);
    if (isStatic) {
        return (React.createElement(StaticMultipleValues, { label: label, name: name, staticFormatter: staticFormatter, isLoading: loading, options: options, multiple: multiple, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
    }
    return (React.createElement(BaseSelect, { ariaLabelledby: ariaLabelledby !== null && ariaLabelledby !== void 0 ? ariaLabelledby : "form-group-".concat(name, "-").concat(uniqueId), ariaLabel: label, id: "form-group-".concat(name, "-").concat(uniqueId), components: components, multiple: multiple, options: options, value: creatable ? undefined : currentValue, onChange: handleChange, isClearable: isClearable, formatter: formatter, disabled: disabled || formDisabled, autoFocus: autoFocus, loading: loading, placeholder: placeholder, creatable: creatable, dataId: dataId, dataTestId: dataTestId, menuPortalTarget: portal }));
});
export var Select = withFormWrapper({ WrappedComponent: LegacySelect });
